"use client";

import type { Theme } from "@mui/material/styles";

import { memo, forwardRef, isValidElement } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";

import { Text } from "../../typography/text";
import { Card } from "../../data-display/card";
import { Icon } from "../../data-display/icon";
import { IconButton } from "../../actions/icon-button";

import type { ToastTone, ToastProps } from "./types";

// ============================================================================
// Defaults por tom
// ============================================================================

/**
 * Ícone padrão por tom. Sobrescritível via prop `icon`.
 *
 * Mantemos um único ícone canônico por tom pra evitar inconsistência entre
 * toasts da plataforma — todo "success" tem o mesmo check, todo "warning"
 * o mesmo triângulo, etc. Usamos os traçados `linear` (outline) — glyph fino,
 * sem tile, coerente com o redesign.
 */
const TONE_ICON: Record<ToastTone, string> = {
  success: "lucide:check",
  info: "lucide:minus",
  warning: "lucide:alert-triangle",
  danger: "lucide:x",
};

/**
 * Disco da marca. `info` é NEUTRO de propósito: ele carrega os resultados que
 * não são ganho nem perda (uma exclusão, por exemplo). Verde é pro que o
 * organizador queria ganhar, nunca pro que ele perdeu.
 */
function toneMarkSx(theme: Theme, tone: ToastTone) {
  if (tone === "info") {
    return {
      bgcolor: alpha(theme.palette.text.primary, 0.06),
      color: theme.palette.text.secondary,
    };
  }
  const color =
    tone === "success"
      ? theme.palette.success.main
      : tone === "warning"
        ? theme.palette.warning.main
        : theme.palette.error.main;
  return { bgcolor: alpha(color, 0.15), color };
}

const DEFAULT_TONE: ToastTone = "info";

/**
 * Métrica do título — denso, na faixa de toast (14–15px), mais perto do
 * Resend que do body padrão (16px). `line-height` apertado pra caber bem.
 */
const TITLE_FONT_PX = { xs: 13.5, md: 13.5 } as const;
const TITLE_LINE_HEIGHT = 1.35;

/**
 * Altura da primeira linha do título — usada pra ancorar glyph e botão de
 * fechar à primeira linha. Assim, no toast de 1 linha eles centram; no de 2+
 * linhas ficam colados no topo (não no centro vertical do bloco).
 */
const TITLE_LINE_BOX = {
  xs: `${TITLE_FONT_PX.xs * TITLE_LINE_HEIGHT}px`,
  md: `${TITLE_FONT_PX.md * TITLE_LINE_HEIGHT}px`,
} as const;

// ============================================================================
// Componente
// ============================================================================

/**
 * **Toast** — feedback transitório de uma ação concluída ou um aviso.
 *
 * Composto inteiramente sobre primitives do design system:
 *  - `<Card variant="filled" />` como container (mesmo radius/sombra dos
 *    outros surfaces da plataforma).
 *  - `<Icon />` à esquerda como glyph fino colorido pelo tom (sem tile),
 *    alinhado ao topo.
 *  - `<Text />` para título + descrição.
 *  - `<IconButton variant="ghost" />` para o fechar.
 *
 * **Não é gerenciado** — o componente só renderiza o card. Para empilhar,
 * auto-dismiss e portal, plugue ele num gerenciador (sonner, notistack,
 * radix, ou um custom). Veja a story `IntegracaoSonner` para exemplo.
 *
 * @example Sucesso simples
 * ```tsx
 * <Toast tone="success" title="Configurações salvas com sucesso!" onClose={dismiss} />
 * ```
 *
 * @example Com descrição + ação
 * ```tsx
 * <Toast
 *   tone="danger"
 *   title="Não foi possível salvar"
 *   description="Verifique sua conexão e tente novamente."
 *   action={{ label: "Tentar de novo", onClick: retry }}
 *   onClose={dismiss}
 * />
 * ```
 */
function ToastImpl(
  {
    title,
    description,
    tone = DEFAULT_TONE,
    icon,
    onClose,
    action,
    progressMs,
    sx,
    className,
  }: ToastProps,
  ref: React.Ref<HTMLDivElement>
) {
  // A cor mora num DISCO de 17px, não num glifo solto: o card já é uma
  // superfície flutuante e não precisa de um segundo sinal (fundo colorido).
  const iconNode = isValidElement(icon) ? (
    icon
  ) : (
    <Box
      aria-hidden
      sx={(theme) => ({
        width: 17,
        height: 17,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...toneMarkSx(theme, tone),
      })}
    >
      <Icon name={typeof icon === "string" ? icon : TONE_ICON[tone]} size={10} tone="inherit" />
    </Box>
  );

  return (
    <Card
      variant="filled"
      radius="md"
      padding="none"
      className={className}
      sx={[
        (theme) => ({
          // Branco no claro, #161616 no escuro: um degrau acima da página, sem
          // o creme do `surfaceAlt` (que é o canvas, não chrome).
          backgroundColor: theme.palette.mode === "light" ? "#FFFFFF" : "#161616",
          border: `1px solid ${theme.palette.mode === "light" ? "#E4E4E4" : "#2E2E2E"}`,
          // Sombra em CAMADAS — o toast flutua sobre a tela inteira e precisa
          // ler como um objeto solto, não como um retângulo colado no fundo.
          // Uma sombra só (por mais forte que seja) dá esse achatamento: falta
          // a mancha larga que descola do fundo e falta a sombra curta de
          // contato, que é o que o olho usa para julgar altura.
          //  1. ambiente: larga e difusa, separa do fundo;
          //  2. corpo: o volume da peça;
          //  3. contato: curta e opaca, ancora a borda de baixo;
          //  4. fio de luz no topo, a aresta virada para cima pegando a luz.
          // Sombra dupla: a mancha que descola do fundo e a curta de contato.
          boxShadow:
            theme.palette.mode === "light"
              ? "0 10px 34px rgba(0,0,0,0.11), 0 2px 8px rgba(0,0,0,0.06)"
              : "0 10px 34px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.35)",
          borderRadius: "11px",
          width: 1,
          maxWidth: 480,
          // Entrada suave ao montar — desce do topo, coerente com a âncora
          // topo-direita (desktop) / topo-centro (mobile). O leave fica com o
          // gerenciador (sonner etc.), que desmonta.
          // Sobe do rodapé, que é de onde o toast agora entra.
          transformOrigin: "center bottom",
          "@keyframes toastIn": {
            from: { opacity: 0, transform: "translateY(10px) scale(0.98)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
          animation: "toastIn 200ms cubic-bezier(0.32, 0.72, 0, 1)",
          willChange: "transform, opacity",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack
        ref={ref}
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{
          p: 1.5,
          pl: 1.625,
          pr: 1.5,
          pb: progressMs != null && progressMs > 0 ? 1 : 1.5,
        }}
      >
        {/* Glyph fino centrado na primeira linha do título; no multi-linha
            fica naturalmente ancorado ao topo. */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            height: TITLE_LINE_BOX,
          }}
        >
          {iconNode}
        </Box>

        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Text
            variant="body"
            weight="medium"
            sx={{ color: "text.primary", fontSize: TITLE_FONT_PX, lineHeight: TITLE_LINE_HEIGHT }}
          >
            {title}
          </Text>
          {description != null && (
            <Text
              variant="caption"
              sx={{ color: "rgba(var(--palette-text-primaryChannel) / 0.6)" }}
            >
              {description}
            </Text>
          )}
        </Stack>

        {action ? (
          <ButtonBase
            onClick={action.onClick}
            sx={(theme) => ({
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.625,
              height: 26,
              px: 1.125,
              borderRadius: "7px",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: 12.5,
              transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
              "&:hover": {
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                color: theme.palette.text.primary,
                borderColor: alpha(theme.palette.text.primary, 0.24),
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            })}
          >
            {action.icon ? <Icon name={action.icon} size={14} tone="inherit" /> : null}
            {action.label}
          </ButtonBase>
        ) : null}

        {onClose ? (
          // Ancorado à primeira linha (igual ao glyph). O botão de 32px é maior
          // que a caixa da linha e transborda simetricamente — centra na 1ª
          // linha no toast de 2+ linhas, em vez de no meio do bloco.
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              height: TITLE_LINE_BOX,
            }}
          >
            <IconButton
              ariaLabel="Fechar notificação"
              size="sm"
              variant="ghost"
              tone="neutral"
              onClick={onClose}
            >
              <Icon name="mingcute:close-line" size="sm" tone="muted" />
            </IconButton>
          </Box>
        ) : null}
      </Stack>

      {/* Barra de tempo: escoa da largura toda a zero na duração do toast, na
          cor do tom. Sem ela o toast some no meio da leitura e o "Desfazer"
          vai junto, sem aviso. `aria-hidden`: é reforço visual do que o
          gerenciador já controla, não informação nova. */}
      {progressMs != null && progressMs > 0 ? (
        <Box
          aria-hidden
          sx={{
            // Respiro nas laterais e na base: encostada nos cantos, a barra
            // brigava com o raio do card e a ponta ficava cortada em bisel.
            px: 1.5,
            pb: 0.75,
            "@keyframes toastProgress": { from: { width: "100%" }, to: { width: "0%" } },
          }}
        >
          <Box
            sx={(theme) => ({
              height: 3,
              width: "100%",
              // Ponta arredondada: a barra encurta pela direita, e um corte
              // reto ali lia como se ela tivesse sido interrompida.
              borderRadius: 999,
              backgroundColor:
                tone === "info"
                  ? alpha(theme.palette.text.primary, 0.35)
                  : theme.palette[tone === "danger" ? "error" : tone].main,
              animation: `toastProgress ${progressMs}ms linear forwards`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.5 },
            })}
          />
        </Box>
      ) : null}
    </Card>
  );
}

export const Toast = memo(forwardRef(ToastImpl));
Toast.displayName = "Toast";
